import ReactMarkdown, { defaultUrlTransform } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import { useEffect, useRef, useState } from 'react'
import 'katex/dist/katex.min.css'
import { CheckOutlined, CopyOutlined } from '@ant-design/icons'
import * as fileApi from '@/api/file'

const splitPathSegments = (path: string): string[] =>
    path.split(/[\\/]+/).filter(Boolean)

const basenameFromPath = (path: string): string => {
    const segments = splitPathSegments(path)
    return segments[segments.length - 1] || path
}

const trailingSegmentsLabel = (path: string, depth: number): string => {
    const segments = splitPathSegments(path)
    if (!segments.length) return path
    const safeDepth = Math.max(1, Math.min(depth, segments.length))
    return segments.slice(-safeDepth).join('/')
}

const computeDisplayLabels = (paths: string[]): string[] => {
    const labels = paths.map((path) => basenameFromPath(path))
    const basenameGroups = new Map<string, number[]>()

    paths.forEach((path, index) => {
        const base = basenameFromPath(path)
        const group = basenameGroups.get(base) || []
        group.push(index)
        basenameGroups.set(base, group)
    })

    basenameGroups.forEach((indices) => {
        if (indices.length <= 1) {
            return
        }

        const pathSegments = indices.map((index) => splitPathSegments(paths[index]))
        const maxDepth = Math.max(...pathSegments.map((segments) => segments.length))
        let resolved = false

        for (let depth = 2; depth <= Math.max(2, maxDepth); depth += 1) {
            const used = new Map<string, number>()
            let hasCollision = false

            indices.forEach((index) => {
                const candidate = trailingSegmentsLabel(paths[index], depth)
                labels[index] = candidate
                const count = used.get(candidate) || 0
                used.set(candidate, count + 1)
                if (count + 1 > 1) {
                    hasCollision = true
                }
            })

            if (!hasCollision) {
                resolved = true
                break
            }
        }

        if (!resolved) {
            indices.forEach((index) => {
                labels[index] = paths[index]
            })
        }
    })

    return labels
}

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'ico', 'tiff', 'tif', 'avif'])

const isImagePath = (src: string): boolean => {
    if (!src.startsWith('/') || src.startsWith('//')) return false
    const ext = src.split('?')[0].split('.').pop()?.toLowerCase() ?? ''
    return IMAGE_EXTENSIONS.has(ext)
}

const ImageBlock = ({ src, alt }: { src?: string; alt?: string }) => {
    const [objectUrl, setObjectUrl] = useState<string | undefined>()

    useEffect(() => {
        if (!src) return
        let revoked = false
        fileApi.getRawFileResourceInBlob(src).then((blob) => {
            if (blob && !revoked) {
                setObjectUrl(window.URL.createObjectURL(blob))
            }
        })
        return () => {
            revoked = true
            setObjectUrl((prev) => {
                if (prev) window.URL.revokeObjectURL(prev)
                return undefined
            })
        }
    }, [src])

    if (!objectUrl) return null
    return <img src={objectUrl} alt={alt} style={{ maxWidth: '100%' }} />
}

const safeDecodeURIComponent = (value: string): string => {
    try {
        return decodeURIComponent(value)
    } catch {
        return value
    }
}

const getActionFromHref = (href?: string): string | null => {
    if (!href?.startsWith('action:')) {
        return null
    }

    return safeDecodeURIComponent(href.slice('action:'.length))
}

const CodeBlock = ({
    className,
    children,
    onPathOpen,
    ...props
}: {
    className?: string
    children?: React.ReactNode
    onPathOpen?: (path: string) => void
} & React.HTMLAttributes<HTMLElement>) => {
    const text = String(children ?? '')

    const unescapeMarkdownLabelForCode = (label: string) =>
        label
            .replace(/\\\\/g, '\\')
            .replace(/\\\[/g, '[')
            .replace(/\\\]/g, ']')

    const nodes: React.ReactNode[] = []
    const re = /\[([^\]]+)\]\(#path=([^\)]+)\)/g
    let lastIndex = 0
    let match: RegExpExecArray | null
    while ((match = re.exec(text)) !== null) {
        const [full, labelRaw, encoded] = match
        const start = match.index
        if (start > lastIndex) nodes.push(text.slice(lastIndex, start))

        const path = safeDecodeURIComponent(encoded)
        const label = unescapeMarkdownLabelForCode(labelRaw)
        nodes.push(
            <a
                key={`${start}:${encoded}`}
                href="#"
                className="path-link"
                data-path={path}
                title={path}
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onPathOpen?.(path)
                }}
            >
                {label}
            </a>
        )

        lastIndex = start + full.length
    }
    if (lastIndex < text.length) nodes.push(text.slice(lastIndex))

    return (
        <code className={className} {...props}>
            {nodes.length > 0 ? nodes : children}
        </code>
    )
}

const PreBlock = ({ children, ...props }: React.HTMLAttributes<HTMLPreElement>) => {
    const [copied, setCopied] = useState(false)
    const preRef = useRef<HTMLPreElement>(null)

    const handleCopy = () => {
        const text = preRef.current?.innerText ?? ''
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        })
    }

    return (
        <div style={{ position: 'relative' }}>
            <button
                onClick={handleCopy}
                title="Copy"
                style={{
                    position: 'absolute',
                    top: '6px',
                    right: '6px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px 4px',
                    opacity: 0.6,
                    fontSize: '13px',
                    lineHeight: 1,
                    color: 'inherit',
                    zIndex: 1,
                }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '0.6')}
            >
                {copied ? <CheckOutlined style={{ color: '#67c23a' }} /> : <CopyOutlined />}
            </button>
            <pre ref={preRef} {...props}>
                {children}
            </pre>
        </div>
    )
}

export const MarkdownRender = ({
    content,
    onClick,
    onPathOpen,
}: {
    content?: string
    onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
    onPathOpen?: (path: string) => void
}) => {

    const [renderContent, setRenderContent] = useState<string>("");


    const schema = {
        ...defaultSchema,
        tagNames: [
            ...(defaultSchema.tagNames ?? []),
            // MathML elements used by KaTeX
            'math', 'mrow', 'mi', 'mn', 'mo', 'mspace', 'mfrac', 'msup', 'msub',
            'msubsup', 'msqrt', 'mover', 'munder', 'munderover', 'mtable', 'mtr',
            'mtd', 'mtext', 'mstyle', 'annotation', 'semantics', 'menclose',
        ],
        attributes: {
            ...defaultSchema.attributes,
            a: ['href', 'title', 'target', 'rel', 'data-path', 'data-action', 'action', 'style', 'class'],
            img: ['src', 'alt', 'title'],
            code: ['className'],
            // Allow KaTeX-generated spans with class/style/aria-hidden
            span: ['className', 'style', 'aria-hidden', 'aria-label'],
            // Allow MathML attributes
            math: ['xmlns', 'display'],
            mspace: ['width', 'height', 'depth'],
            mstyle: ['mathcolor', 'mathbackground', 'mathsize', 'mathvariant'],
            annotation: ['encoding'],
            '*': ['className', 'style'],
        },
        protocols: {
            ...defaultSchema.protocols,
            href: [...(defaultSchema.protocols?.href ?? []), 'action'],
        },
    }

    useEffect(() => {
        if (content) {
            const escapeMarkdownLabel = (label: string) =>
                label
                    .replace(/\\/g, '\\\\')
                    .replace(/\[/g, '\\[')
                    .replace(/\]/g, '\\]')

            const toPathLinkMarkdown = (path: string, displayLabel?: string) => {
                const finalLabel = displayLabel || basenameFromPath(path)
                const safeDisplayLabel = escapeMarkdownLabel(finalLabel)
                const encoded = encodeURIComponent(path)
                return `[${safeDisplayLabel}](#path=${encoded})`
            }

            const pathPattern = /<Path>(.*?)<\/Path>|&lt;Path&gt;(.*?)&lt;\/Path&gt;/g
            const pathValues: string[] = []

            content.replace(pathPattern, (_match, rawPath, escapedPath) => {
                const capturedPath = (rawPath ?? escapedPath ?? '').trim()
                pathValues.push(capturedPath)
                return _match
            })

            const displayLabels = computeDisplayLabels(pathValues)
            let pathIndex = 0

            const temp = content.replace(pathPattern, (_match, rawPath, escapedPath) => {
                const capturedPath = (rawPath ?? escapedPath ?? '').trim()
                const label = displayLabels[pathIndex] || basenameFromPath(capturedPath)
                pathIndex += 1
                return toPathLinkMarkdown(capturedPath, label)
            })

            setRenderContent(temp);
        }
    }, [content]);

    return (
        <div className="markdown-content w-full h-full overflow-auto" onClick={e => onClick?.(e)}>
            <ReactMarkdown
                urlTransform={(url) => {
                    if (url.startsWith('action:')) {
                        return url
                    }

                    return defaultUrlTransform(url)
                }}
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex, [rehypeSanitize, schema]]}
                components={{
                    p: ({ children, ...props }) => (
                        <p style={{ whiteSpace: 'pre-wrap' }} {...props}>
                            {children}
                        </p>
                    ),
                    ul: ({ children, ...props }) => (
                        <ul
                            style={{
                                listStyleType: 'disc',
                                listStylePosition: 'outside',
                                paddingLeft: '2em',
                                marginBottom: '1em',
                            }}
                            {...props}
                        >
                            {children}
                        </ul>
                    ),
                    ol: ({ children, ...props }) => (
                        <ol
                            style={{
                                listStyleType: 'decimal',
                                listStylePosition: 'outside',
                                paddingLeft: '2em',
                                marginBottom: '1em',
                            }}
                            {...props}
                        >
                            {children}
                        </ol>
                    ),
                    a: (props) => {
                        const href = typeof props.href === 'string' ? props.href : undefined
                        const action = getActionFromHref(href)
                        // Resolve the path: either explicit #path= encoding, or a plain absolute path href (e.g. [label](/some/file.xyz))
                        const resolvedPathHref = (() => {
                            if (href?.startsWith('#path=')) {
                                return safeDecodeURIComponent(href.slice('#path='.length))
                            }
                            // Plain absolute-path hrefs that look like filesystem paths
                            if (href?.startsWith('/') && !href.startsWith('//')) {
                                return safeDecodeURIComponent(href)
                            }
                            return null
                        })()
                        if (resolvedPathHref !== null) {
                            const path = resolvedPathHref

                            return (
                                <a
                                    href="#"
                                    className="path-link"
                                    data-path={path}
                                    title={path}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        onPathOpen?.(path)
                                    }}
                                >
                                    {props.children}
                                </a>
                            )
                        }

                        if (action !== null) {
                            return (
                                <a
                                    href={href}
                                    data-action={action}
                                    ref={(node) => {
                                        if (node) {
                                            node.setAttribute('action', action)
                                        }
                                    }}
                                    onClick={(e) => {
                                        e.preventDefault()
                                    }}
                                >
                                    {props.children}
                                </a>
                            )
                        }

                        return (
                            <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {props.children}
                            </a>
                        )
                    },
                    pre: ({ children, ...props }) => (
                        <PreBlock {...props}>
                            {children}
                        </PreBlock>
                    ),
                    img: ({ src, alt }: React.ImgHTMLAttributes<HTMLImageElement>) => {
                        const resolvedSrc = src ? safeDecodeURIComponent(src) : undefined
                        if (resolvedSrc && isImagePath(resolvedSrc)) {
                            return <ImageBlock src={resolvedSrc} alt={alt} />
                        }
                        return <img src={src} alt={alt} style={{ maxWidth: '100%' }} />
                    },
                    code: ({ inline, className, children, ...props }: { inline?: boolean; className?: string; children?: React.ReactNode } & React.HTMLAttributes<HTMLElement>) => {
                        if (inline) {
                            return (
                                <code className={className} {...props}>
                                    {children}
                                </code>
                            )
                        }

                        return (
                            <CodeBlock className={className} onPathOpen={onPathOpen} {...props}>
                                {children}
                            </CodeBlock>
                        )
                    }
                }}

            >
                {renderContent}
            </ReactMarkdown>
        </div>

    )
}